import React, { lazy, Suspense, useContext } from "react";
import clsx from "clsx";
import { useInView } from "react-intersection-observer";
import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import PageContext from "@PageContext";
import WebProfileContext from "@WebProfileContext";
import useLocals from "@micado-digital/react-ui/hooks/useLocals";
import localDE from "../../../locales/de.json";
import localEN from "../../../locales/en.json";

const ReactSVGIcon = lazy(() =>
	import(/* webpackChunkName: "reactSVGIcon" */ "@micado-digital/react-svg-icon/ReactSVGIcon")
);
const ReactLink = lazy(() =>
	import(/* webpackChunkName: "reactLink" */ "@micado-digital/react-link/ReactLink")
);
const Team = lazy(() => import(/* webpackChunkName: "team" */ "./team"));
const ReactFooter02 = lazy(() => import(/* webpackChunkName: "footer" */ "./footer"));

const useStyles = makeStyles(theme => ({
	wrapper: {
		marginTop: "auto",
		position: "relative",
		zIndex: 2,
		"&:before": {
			background:
				"linear-gradient(180deg, rgba(32, 48, 61, 0) 0%, rgba(32, 48, 61, 0.7) 100%)",
			content: "''",
			height: "100%",
			left: 0,
			mixBlendMode: "multiply",
			position: "absolute",
			top: 0,
			width: "100%",
			zIndex: -1
		}
	},
	wrapperVisible: {
		backgroundImage: "url('/img/footer-bg-mobile.png')",
		backgroundPosition: "top center",
		backgroundRepeatX: "repeat",
		backgroundRepeatY: "no-repeat",
		backgroundSize: "cover",
		[theme.breakpoints.up(480)]: {
			backgroundSize: "contain"
		}
	},
	contact: {
		borderTop: "1px solid #364E61",
		paddingTop: theme.spacing(3),
		[theme.breakpoints.up("md")]: {
			borderTop: "none",
			paddingTop: 0
		},
		"& .mco-footer-02__custom__contact-label": {
			color: theme.palette.secondary.light,
			fontSize: 16,
			letterSpacing: "0.15em",
			marginBottom: theme.spacing(2.5),
			textTransform: "uppercase"
		}
	},
	links: {
		marginBottom: theme.spacing(1),
		"& a": {
			alignItems: "center",
			display: "inline-flex",
			fontSize: 20,
			transition: theme.transitions.create("color", {
				duration: theme.transitions.duration.complex
			}),
			[theme.breakpoints.up("xl")]: {
				fontFamily: "'Marat-Regular'",
				fontSize: 35
			}
		},
		"& a:hover": {
			color: "#FFFFFF"
		},
		"& a svg path": {
			transition: theme.transitions.create("fill", {
				duration: theme.transitions.duration.complex
			})
		},
		"& a:hover svg path": {
			fill: "#FFFFFF"
		}
	},
	icon: {
		marginRight: theme.spacing(2),
		[theme.breakpoints.up("xl")]: {
			marginRight: theme.spacing(3.5),
			"& svg": {
				width: "32px !important"
			}
		}
	},
	button: {
		fontSize: 14,
		marginTop: theme.spacing(2),
		padding: 0,
		transition: theme.transitions.create("color", {
			duration: theme.transitions.duration.complex
		}),
		"& .MuiButton-endIcon": {
			marginLeft: theme.spacing(2)
		},
		"&:hover": {
			background: "none",
			color: "#FFFFFF"
		}
	},
	team: {
		pointerEvents: "none",
		[theme.breakpoints.up("lg")]: {
			marginTop: -160
		},
		"& > div": {
			pointerEvents: "all"
		}
	}
}));

const Footer = () => {
	const css = useStyles();
	const [pageData] = useContext(PageContext);
	const [profileData] = useContext(WebProfileContext);
	const { fields, lang = "de", menuGroups } = pageData || {};
	const [ref, inView] = useInView({
		threshold: 0
	});
	const l = useLocals({
		lang,
		res: {
			de: localDE,
			en: localEN
		}
	});

	const footermenu = menuGroups?.find(item => item.title === "Hauptmenü");
	const metamenu = menuGroups?.find(item => item.title === "Metamenü");
	const socialLinks = profileData?.links;
	const teamFoto = fields?.find(item => item.tag === "footer-team");

	let whatsappLink = null;

	profileData?.links?.filter(item => {
		if (item?.title === "WhatsApp") {
			whatsappLink = item?.url;
		}
		return null;
	});

	const contactLink = profileData?.customLinks?.find(
		item => item?.identifier === "contact"
	)?.url;

	return (
		<div ref={ref}>
			<div className={clsx("mco-footer-02__team", css.team)}>
				<Suspense fallback={<></>}>
					<Team lang={lang} team={teamFoto?.value} />
				</Suspense>
			</div>
			<div
				className={clsx("mco-footer-wrapper", css.wrapper, { [css.wrapperVisible]: inView })}
			>
				<Suspense fallback={<></>}>
					<ReactFooter02
						footermenu={footermenu}
						lang={lang}
						logo={"/img/logo-small.svg"}
						logoHeight={52}
						logoWidth={60}
						micado={false}
						maxWidth="xl"
						metamenu={metamenu}
						socialLinks={socialLinks}
					>
						<div className={clsx("mco-footer-02__custom")}>
							<div className={clsx("mco-footer-02__custom__contact", css.contact)}>
								<Typography className="mco-footer-02__custom__contact-label" variant="body1">
									{l("footer.contact")}
								</Typography>
								{whatsappLink && (
									<Typography
										className={clsx("mco-footer-02__custom__contact-whatsapp", css.links)}
										variant="body1"
										component="div"
									>
										<a href={whatsappLink} target="_blank" rel="noreferrer">
											<Suspense fallback={<></>}>
												<ReactSVGIcon
													color="primary"
													className={css.icon}
													size={24}
													src="/img/icons/whatsapp.svg"
												/>
											</Suspense>
											Whatsapp LiveChat
										</a>
									</Typography>
								)}
								{profileData?.phone && (
									<Typography
										className={clsx("mco-footer-02__custom__contact-phone", css.links)}
										variant="body1"
										component="div"
									>
										<a href={"tel:" + profileData?.phone?.replace(/ /g, "")}>
											<Suspense fallback={<></>}>
												<ReactSVGIcon
													color="primary"
													className={css.icon}
													size={24}
													src="/img/icons/phone.svg"
												/>
											</Suspense>
											{profileData?.phone}
										</a>
									</Typography>
								)}
								{profileData?.mail && (
									<Typography
										className={clsx("mco-footer-02__custom__contact-mail", css.links)}
										variant="body1"
										component="div"
									>
										<a href={"mailto:" + profileData?.mail}>
											<Suspense fallback={<></>}>
												<ReactSVGIcon
													color="primary"
													className={css.icon}
													size={24}
													src="/img/icons/mail.svg"
												/>
											</Suspense>
											{profileData?.mail}
										</a>
									</Typography>
								)}
								<Suspense fallback={<></>}>
									<ReactLink to={contactLink}>
										<Button
											className={css.button}
											variant="text"
											color="primary"
											size="small"
											endIcon={<ReactSVGIcon size={14} src="/img/icons/chevron-right.svg" />}
										>
											{l("footer.contactData")}
										</Button>
									</ReactLink>
								</Suspense>
							</div>
						</div>
					</ReactFooter02>
				</Suspense>
			</div>
		</div>
	);
};

export default Footer;
